import {Injectable} from "@angular/core";
import {LngLat} from "mapbox-gl";
import {LABEL_LAYER, LABELS_SOURCE_ID, POLYGON_INFO_FEATURE_TYPE} from "./consts/map.constants";
import {Feature} from "@turf/turf";
import {uniqueId} from "lodash";
import {MapLabelModel} from "./model/map-label";

@Injectable({
    providedIn: 'root'
})
export class LabelsHandler {
    constructor() { }

    getLabelsFromSource(map: mapboxgl.Map): MapLabelModel[] {
        const geoJsonSource = map.getSource(LABELS_SOURCE_ID) as any;
        geoJsonSource._data as GeoJSON.FeatureCollection;
        const labels: MapLabelModel[] = [];
        if (geoJsonSource && geoJsonSource._data && geoJsonSource._data.features) {
            for (const feature of geoJsonSource._data.features) {
                if (feature.geometry) {
                    const coords = feature.geometry.coordinates;
                    const labelText = feature.properties["label"];
                    labels.push({
                        text: labelText,
                        latitude: coords[1],
                        longitude: coords[0],
                    });
                }
            }
        }
        return labels;
    }

    addLabelSourceData(map: mapboxgl.Map) {
        const geoJsonSource = map.getSource(LABELS_SOURCE_ID) as any;
        let featureCollection: GeoJSON.FeatureCollection = null;
        if (!geoJsonSource) {
            featureCollection = {
                type: 'FeatureCollection',
                features: [],
            };
            map.addSource(LABELS_SOURCE_ID, {
                type: 'geojson',
                data: featureCollection,
            });
            this.addLabelLayer(map);
            let customListenerWithMap = (event: any) => {
                this.onLabelMouseDown(event, map);
            }
            map.on('mousedown', LABEL_LAYER, customListenerWithMap);
        }
    }

    addLabelToMap(map: mapboxgl.Map, labelText: string, latitude: number, longitude: number) {
        const labelFeature: Feature = {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [longitude, latitude]
            },
            properties: {
                label: labelText,
                id: uniqueId(),
            }
        };
        const geoJsonSource = map.getSource(LABELS_SOURCE_ID) as any;
        if (geoJsonSource) {
            const data = geoJsonSource._data as GeoJSON.FeatureCollection;
            data.features.push(labelFeature as any);
            geoJsonSource.setData(data);
        }
    }

    private addLabelLayer(map: mapboxgl.Map) {
        if (map.getLayer(LABEL_LAYER)) {
            map.removeLayer(LABEL_LAYER);
        }
        map.addLayer({
            id: LABEL_LAYER,
            type: 'symbol',
            source: LABELS_SOURCE_ID,
            layout: {
                'text-field': ['get', 'label'],
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-anchor': 'center',
                'text-size': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0.1, 4,
                    2, 5,
                    2.5, 8,
                    3, 12,
                    15, 12
                ],
                'text-allow-overlap': false,
                'text-ignore-placement': false
            },
            paint: {
                'text-color': '#01090d',
                'text-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    1.8, 0,
                    3, 1,
                ],
                'text-halo-width': 3,
                'text-halo-color': 'rgb(255,255,255)',
            },
        });
    }

    public onLabelMouseDown = (e: any, map: mapboxgl.Map) => {
        e.preventDefault();
        const feature = e.features[0];
        if (feature) {
            try {
                let customMarkerDragListener = (event: any) => {
                    this.onMarkerDrag(event, map, feature);
                }
                let customMarkerDragEndListener = (event: any) => {
                    this.onMarkerDragEnd(event, map, feature, customMarkerDragListener, customMarkerDragEndListener);
                }
                map.on('mousemove', customMarkerDragListener);
                map.on('mouseup', customMarkerDragEndListener);

                map.dragPan.disable();
            } catch (error) {
                console.error('Error parsing datacenter in onMarkerMouseDown:', error);
            }
        }
    };

    private onMarkerDrag = (e: any, map: mapboxgl.Map, feature: any) => {
        window.requestAnimationFrame(() => {
            const coords = e.lngLat;
            map.getCanvasContainer().style.cursor = 'grabbing';
            const source = map.getSource(LABELS_SOURCE_ID) as any;
            if (!source || !source._data) {
                console.warn('DCMouseEventsHandler: Source or data not available');
                return;
            }
            const geojson = source._data;
            let featureToUpdate = geojson.features.find(f => f.properties["id"] === feature.properties["id"]);

            if (featureToUpdate) {
                featureToUpdate.geometry.coordinates = [coords.lng, coords.lat];
                featureToUpdate.properties["isDragging"] = true;
                source.setData(geojson);
            }
        });
    };

    private onMarkerDragEnd = (e: any, map: mapboxgl.Map, feature: any,
        markerDragListener, markerDragEndListener) => {
        const coords = e.lngLat;
        const source = map.getSource(LABELS_SOURCE_ID) as any;
        const geojson = source._data;
        let featureToUpdate = geojson.features.find(f => f.properties["id"] === feature.properties["id"]);

        const isDragging: boolean = featureToUpdate.properties["isDragging"];
        if (isDragging) {
            featureToUpdate.properties["isDragging"] = false;
            source.setData(geojson);
            map.dragPan.enable();
            map.getCanvas().style.cursor = 'pointer';
        }
        map.off('mousemove', markerDragListener);
        map.off('mouseup', markerDragEndListener);
    }

}